import React, { useState, useEffect } from 'react';
import './QuestionTopicSelection.css';

/**
 * DiagnosticTopicsModal Component
 * Modal for selecting diagnostic (child) topics after main topic is selected
 * Replaces the Angular modal from question-topic-selection.html
 */
const DiagnosticTopicsModal = ({ show, onHide, onSkip, mainTopic, currentTopics, onSave }) => {
  const [firstLayerChildren, setFirstLayerChildren] = useState([]);
  const [secondLayerChildren, setSecondLayerChildren] = useState([]);

  useEffect(() => {
    if (show && mainTopic) {
      // Initialize with existing selections
      // Only mark as selected if the topic exists AND is NOT the main topic (IsMain !== true)
      const firstLayer = (mainTopic.FirstLayerChildren || []).map(topic => {
        const existingTopic = currentTopics.find(t => t.ID === topic.ID);
        return {
          ...topic,
          selected: topic.selected || (existingTopic && existingTopic.IsMain !== true)
        };
      });
      const secondLayer = (mainTopic.SecondLayerChildren || []).map(topic => {
        const existingTopic = currentTopics.find(t => t.ID === topic.ID);
        return {
          ...topic,
          selected: topic.selected || (existingTopic && existingTopic.IsMain !== true)
        };
      });

      setFirstLayerChildren(firstLayer);
      setSecondLayerChildren(secondLayer);
    }
  }, [show, mainTopic, currentTopics]);

  /**
   * Handle checkbox toggle for a topic
   */
  const handleTopicToggle = (topicId, isFirstLayer) => {
    if (isFirstLayer) {
      setFirstLayerChildren(prev => 
        prev.map(topic => 
          topic.ID === topicId 
            ? { ...topic, selected: !topic.selected }
            : topic
        )
      );
    } else {
      setSecondLayerChildren(prev => 
        prev.map(topic => 
          topic.ID === topicId 
            ? { ...topic, selected: !topic.selected }
            : topic
        )
      );
    }
  };

  /**
   * Handle topic click (toggle checkbox)
   */
  const handleTopicClick = (topicId, isFirstLayer) => {
    handleTopicToggle(topicId, isFirstLayer);
  };

  /**
   * Handle OK button click
   */
  const handleOk = () => {
    if (onSave) {
      onSave(firstLayerChildren, secondLayerChildren);
    }
  };

  if (!show) return null;

  return (
    <div 
      className="modal fade show" 
      id="chooseDiagnosticeTopics" 
      style={{ display: 'block', marginTop: '40px', overflowY: 'auto' }}
      tabIndex="-1"
      role="dialog"
      onClick={(e) => {
        // Prevent clicks on modal container from bubbling to backdrop
        e.stopPropagation();
      }}
    >
      <div className="modal-dialog" style={{ width: '90%' }}>
        <div 
          className="modal-content"
          onClick={(e) => {
            // Prevent clicks inside modal from bubbling to backdrop
            e.stopPropagation();
          }}
        >
          <div className="modal-header">
            <button 
              type="button" 
              className="close" 
              onClick={onHide}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 className="modal-title">Select Diagnostic Topics</h4>
          </div>

          <div className="modal-body">
            <div>
              <div>
                <span className="headText">Please select diagnostics topics :</span>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  You can select diagnostic topics now or skip and add them later.
                </p>
              </div>

              <div className="tree top-margin">
                <ul>
                  <li className="parentTopic">
                    <span>{mainTopic.Name || mainTopic.Desc}</span>
                  </li>
                  <ul>
                    {/* First Layer Sub Topics */}
                    {firstLayerChildren.length > 0 && (
                      <li>
                        {firstLayerChildren.map((topic, key) => (
                          <span
                            key={topic.ID || key}
                            className={`right-margin digTopic bottom-margin ${topic.selected ? 'selected' : ''}`}
                            style={{ 
                              background: topic.selected ? '#B0CFD8' : 'inherit',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTopicClick(topic.ID, true);
                            }}
                          >
                            <input
                              type="checkbox"
                              className="right-margin1"
                              checked={topic.selected || false}
                              onChange={() => handleTopicToggle(topic.ID, true)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <i className="clickable">{topic.Desc}</i>
                          </span>
                        ))}
                      </li>
                    )}

                    {/* Second Layer Sub Topics */}
                    {secondLayerChildren.length > 0 && (
                      <li>
                        {secondLayerChildren.map((topic, key) => (
                          <span
                            key={topic.ID || key}
                            className={`right-margin digTopic bottom-margin ${topic.selected ? 'selected' : ''}`}
                            style={{ 
                              background: topic.selected ? '#B0CFD8' : 'inherit',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTopicClick(topic.ID, false);
                            }}
                          >
                            <input
                              type="checkbox"
                              className="right-margin1"
                              checked={topic.selected || false}
                              onChange={() => handleTopicToggle(topic.ID, false)}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <i className="clickable">{topic.Desc}</i>
                          </span>
                        ))}
                      </li>
                    )}
                  </ul>
                </ul>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button 
              className="btn btn-success pull-right" 
              onClick={handleOk}
              style={{ marginLeft: '10px' }}
            >
              OK
            </button>
            {onSkip && (
              <button 
                className="btn btn-warning pull-right" 
                onClick={onSkip}
                style={{ marginLeft: '10px' }}
                title="Skip adding diagnostic topics for now. You can add them later."
              >
                SKIP
              </button>
            )}
            <button 
              className="btn btn-default pull-right" 
              onClick={onHide}
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
      <div 
        className="modal-backdrop fade show" 
        onClick={(e) => {
          // Only close if clicking directly on backdrop, not if event bubbled from modal content
          if (e.target === e.currentTarget) {
            onHide();
          }
        }}
      ></div>
    </div>
  );
};

export default DiagnosticTopicsModal;

